import { Component, h } from 'preact'
import classnames from 'classnames'
import Button from '@insided/control/uikit/Button'
import ColorPicker from './ColorPicker'
import { StylingConfigType } from './configurator'
import InputGroup from '../../control-components/ui-components/InputGroup'
import { generateCssClassName } from './generateCssClassName'
import ConfigurationSetting from './ConfigurationSetting'

interface CardStatusData {
  key: string
  checkbox?: {
    key: string
    title: string
  }
}

type CardStatusType = 'default' | 'hover' | 'click'
type CardOptionsType = 'background' | 'title' | 'text' | 'border' | 'shadow'

interface CardsTheme {
  default: {
    background: CardStatusData
    title: CardStatusData
    text: CardStatusData
    border: CardStatusData
    shadow: CardStatusData
  }
  hover: {
    background: CardStatusData
    title: CardStatusData
    text: CardStatusData
    border: CardStatusData
    shadow: CardStatusData
  }
  click: {
    background: CardStatusData
    title: CardStatusData
    text: CardStatusData
    border: CardStatusData
    shadow: CardStatusData
  }
}

interface GenericSetting {
  key: string
  label: string
}

const GENERIC_SETTINGS: GenericSetting[] = [
  {
    key: 'config-card-border-radius',
    label: 'Border radius'
  },
  {
    key: 'config-card-border-width',
    label: 'Border width'
  }
]

const CARDS: CardsTheme = {
  default: {
    background: {
      key: 'config-card-background-color'
    },
    title: {
      key: 'config-card-title-color',
      checkbox: {
        title: 'Use heading color',
        key: 'config-header-color'
      }
    },
    text: {
      key: 'config-card-text-color',
      checkbox: {
        title: 'Use text color',
        key: 'config--main-color-night'
      }
    },
    border: {
      key: 'config-card-border-color',
      checkbox: {
        title: 'Use border color',
        key: 'config--main-border-base-color'
      }
    },
    shadow: {
      key: 'config-card-shadow'
    }
  },
  hover: {
    background: {
      key: 'config-card-hover-background-color'
    },
    title: {
      key: 'config-card-hover-title-color',
      checkbox: {
        title: 'Use heading color',
        key: 'config-header-color'
      }
    },
    text: {
      key: 'config-card-hover-text-color',
      checkbox: {
        title: 'Use text color',
        key: 'config--main-color-night'
      }
    },
    border: {
      key: 'config-card-hover-border-color',
      checkbox: {
        title: 'Use border color',
        key: 'config--main-border-base-color'
      }
    },
    shadow: {
      key: 'config-card-hover-shadow'
    }
  },
  click: {
    background: {
      key: 'config-card-active-background-color'
    },
    title: {
      key: 'config-card-active-title-color',
      checkbox: {
        title: 'Use heading color',
        key: 'config-header-color'
      }
    },
    text: {
      key: 'config-card-active-text-color',
      checkbox: {
        title: 'Use text color',
        key: 'config--main-color-night'
      }
    },
    border: {
      key: 'config-card-active-border-color',
      checkbox: {
        title: 'Use border color',
        key: 'config--main-border-base-color'
      }
    },
    shadow: {
      key: 'config-card-hover-shadow'
    }
  }
}

const CARD_STATUSES: CardStatusType[] = ['default', 'hover', 'click']
const CARD_COLOR_OPTIONS: CardOptionsType[] = ['background', 'title', 'text', 'border', 'shadow']

interface Props {
  updatedStyling: StylingConfigType
  defaultStyling: StylingConfigType
  updatedColors: StylingConfigType
  onUpdate: (updatedStyling: StylingConfigType) => void
}

interface State {
  enabledColorSelector: null | string
  activeTab: CardStatusType
}

const QA_CLASSNAME = 'theme-configuration_cards'

export default class Cards extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      enabledColorSelector: null,
      activeTab: 'default'
    }

    this.updateStyling = this.updateStyling.bind(this)
    this.resetStyling = this.resetStyling.bind(this)
  }

  updateStyling(key: string, color: string | null) {
    this.props.onUpdate({ ...this.props.updatedStyling, ...{ [key]: color } })
  }

  resetStyling() {
    this.props.onUpdate(this.props.defaultStyling)
  }

  render() {
    const { updatedStyling, updatedColors } = this.props
    const { enabledColorSelector, activeTab } = this.state
    return (
      <div className='theme-configuration'>
        <div className={classnames('theme-configuration-setting', 'box__pad', 'with-border', QA_CLASSNAME)}>
          <div className='theme-configuration_info-box'>
            <strong>✨ Which cards does this styling apply to?</strong>
            <p>
              Card style applies to all the widgets in card style, e.g. quick links, featured topics, community
              categories, knowledge base, etc
            </p>
          </div>
          <div className='theme-configuration-generic-settings'>
            {GENERIC_SETTINGS.map((genericCardSetting: GenericSetting) => (
              <ConfigurationSetting key={genericCardSetting.key} label={genericCardSetting.label}>
                <div>
                  <InputGroup
                    title=''
                    type='number'
                    min={0}
                    onInput={(e) => this.updateStyling(genericCardSetting.key, String(e.target.value) + 'px')}
                    value={updatedStyling[genericCardSetting.key]?.toString().replace('px', '')}
                  />
                  <div>px</div>
                </div>
              </ConfigurationSetting>
            ))}
          </div>
          <div className='theme-configuration_tabs'>
            {CARD_STATUSES.map((tab) => (
              <div
                key={tab}
                onClick={() => this.setState({ activeTab: tab })}
                className={classnames('theme-configuration_tab', {
                  'active-tab': activeTab === tab
                })}
              >
                {tab}
              </div>
            ))}
          </div>
          <ul className={`theme-configuration-list theme-configuration-${activeTab}`}>
            {CARD_COLOR_OPTIONS.map((cardColorOption) => {
              const card = CARDS[activeTab][cardColorOption]

              if (cardColorOption === 'shadow') {
                return (
                  <li className='theme-configuration-list-item_setting shadow'>
                    <label>
                      <div>
                        {cardColorOption} &ndash; {activeTab}
                      </div>
                      <div className='theme-configuration-list-item_setting_subtext'>
                        Color x-axis y-axisradius, eg. rgba(0,0,0,0.2) 0 2px 4px
                      </div>
                    </label>
                    <InputGroup
                      title=''
                      type='text'
                      placeholder='rgba(0,0,0,0.2) 0 2px 4px'
                      onInput={(e) => this.updateStyling(card.key, e.target.value)}
                      value={updatedStyling[card.key] || ''}
                    />
                  </li>
                )
              }

              return (
                <li
                  key={cardColorOption}
                  className={classnames('theme-configuration-list-item_setting', generateCssClassName(cardColorOption))}
                >
                  <label>
                    {cardColorOption} Color &ndash; {activeTab}
                  </label>
                  <ColorPicker
                    color={
                      !!card.checkbox && updatedStyling[card.key] === null
                        ? updatedColors[card.checkbox.key]
                        : updatedStyling[card.key]
                    }
                    disabled={!!card.checkbox && updatedStyling[card.key] === null}
                    colorKey={card.key}
                    disableColorSelector={() => this.setState({ enabledColorSelector: null })}
                    setColorSelector={() => this.setState({ enabledColorSelector: card.key })}
                    updateColor={(color: string) => this.updateStyling(card.key, color)}
                    isColorSelectorEnabled={enabledColorSelector === card.key}
                  />
                  {CARDS[activeTab][cardColorOption].checkbox ? (
                    <div className='use-default'>
                      <InputGroup
                        inline
                        title={card.checkbox?.title}
                        type='checkbox'
                        checked={updatedStyling[card.key] === null ? 'checked' : ''}
                        onChangeCheckbox={(e) => {
                          this.updateStyling(
                            card.key,
                            e.target.checked ? null : card.checkbox ? updatedColors[card.checkbox.key] : null
                          )
                        }}
                      />
                    </div>
                  ) : null}
                </li>
              )
            })}
          </ul>
        </div>
        <div className='box__pad theme-configuration-reset'>
          <Button variant='warning' size='medium' onClick={this.resetStyling}>
            Reset to defaults
          </Button>
        </div>
      </div>
    )
  }
}
